import React, { useMemo } from "react";
import {
  Payee,
  PayeeEligibilityRequirement,
  PayeeEngagement,
  PayeeEngagementPaymentsEligibility,
  PayeeEngagementStatus,
  RequirementStatus
} from "@wingspanhq/wingspan-api-sdk";
import {
  WSElementProps,
  WSTable,
  WSTableCell
} from "@wingspanhq/fe-component-library";
import { buildPayeeDetailsEngagementDetailsPath } from "../../utils";
import { useHistory } from "react-router-dom";
import { useXWingspanPayerId } from "./useXWingspanPayerId";

// v3 archive bucket: v1 `Inactive` maps to the v3 terminal/suspended states.
const ARCHIVED_STATUSES: PayeeEngagementStatus[] = [
  PayeeEngagementStatus.SUSPENDED,
  PayeeEngagementStatus.TERMINATED
];

export const EngagementsTable: React.FC<
  {
    payee: Payee;
    engagements: PayeeEngagement[];
    // Per-engagement eligibility requirements, keyed by `PayeeEngagement.id`.
    // v3 `PayeeEngagement` carries only `requirementIds` + the
    // `areAllRequirementsComplete` rollup, so the per-requirement completed
    // count comes from `listPayeeEligibilityRequirements` (fetched by the hub).
    requirementsByEngagementId: Record<string, PayeeEligibilityRequirement[]>;
  } & WSElementProps
> = props => {
  const history = useHistory();
  const xWingspanPayerId = useXWingspanPayerId();

  const tableData = useMemo(() => {
    return props.engagements.map(engagement => ({
      id: engagement.id!,
      data: engagement
    }));
  }, [props.engagements]);

  return (
    <WSTable<PayeeEngagement>
      tableData={tableData}
      columns={[
        {
          config: {
            header: { text: "Name" },
            gridTemplateSizeMax: "1fr"
          },
          renderFunction: ({ data }) => (
            <WSTableCell text={data.engagementName ?? ""} />
          )
        },
        {
          config: {
            hideOnScreens: ["XS"],
            header: { text: "Requirements" },
            gridTemplateSizeMax: "1fr"
          },
          renderFunction: ({ data }) => {
            const requirements = data.id
              ? (props.requirementsByEngagementId[data.id] ?? [])
              : [];
            const allLength = requirements.length;
            const completedLength = requirements.filter(
              r => r.status === RequirementStatus.COMPLETED
            ).length;
            const isComplete = completedLength === allLength && allLength > 0;

            const notEligible =
              data.paymentsEligibility ===
              PayeeEngagementPaymentsEligibility.NOT_ELIGIBLE;
            const secondaryText = notEligible
              ? "Not eligible for payments"
              : "Eligible for payments";
            const secondaryTextColor = notEligible ? "amber400" : undefined;

            if (data.status && ARCHIVED_STATUSES.includes(data.status)) {
              return (
                <WSTableCell
                  pill={{
                    theme: "neutral",
                    text: "Archived",
                    icon: "archive"
                  }}
                />
              );
            }

            if (allLength === 0) {
              return (
                <WSTableCell
                  pill={{
                    theme: "neutral",
                    text: "None required",
                    icon: "info-circle"
                  }}
                  secondaryText={secondaryText}
                  secondaryTextColor={secondaryTextColor}
                />
              );
            }

            return (
              <WSTableCell
                secondaryText={secondaryText}
                secondaryTextColor={secondaryTextColor}
                pill={{
                  theme: isComplete ? "success" : "warning",
                  text: `${completedLength} of ${allLength} Complete`,
                  icon: true
                }}
              />
            );
          }
        }
      ]}
      onRowClick={({ data }) => {
        // TODO(v3): the engagement-detail screen is still v1 and keys on the v1
        // `payerPayeeEngagementId`; this now passes the v3 `PayeeEngagement.id`.
        // Verify the id-space alignment (or migrate the detail screen) before
        // enabling row actions. See V3_MIGRATION_TRACKER.md · payee-engagements.
        history.push({
          pathname: buildPayeeDetailsEngagementDetailsPath(
            props.payee.id!,
            data.id!
          ),
          search: xWingspanPayerId ? `?payerId=${xWingspanPayerId}` : ""
        });
      }}
      {...props}
    />
  );
};
